import { Flex } from "@mantine/core";
import { usePrevious } from "@mantine/hooks";
import { useEffect, useMemo, useRef, type PropsWithChildren } from "react";
import { TypingIndicator } from "../TypingIndicator";
import { RequestStates } from "@/types";
import { getTestIds } from "./helpers";
import styles from "./MessageWrapper.module.css";

interface MessageWrapperProps {
  /** Company name to display above incoming messages */
  company?: string;
  /** Whether this is an incoming (assistant) or outgoing (user) message */
  incoming?: boolean;
  /** Message index for test IDs */
  index: number;
  /** Callback when message finishes loading */
  onFinishedLoading?: () => void;
  /** Loading state of the message */
  status?: RequestStates;
  /**
   * AS-1816: opt out of the rectangular `chatBubble` wrapper for messages
   * that render their own self-contained visual (e.g. the circular voice
   * playback button). The container alignment + animation still applies;
   * only the bubble background/padding/radius is skipped.
   */
  bare?: boolean;
}

/**
 * Wrapper component for chat messages with bubble styling and animations
 */
export const MessageWrapper = ({
  children,
  company,
  incoming = false,
  index,
  onFinishedLoading,
  status = RequestStates.Success,
  bare = false,
}: PropsWithChildren<MessageWrapperProps>) => {
  const testIds = useMemo(() => getTestIds(index), [index]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previousStatus = usePrevious(status);
  const isLoading = status === RequestStates.Loading;

  useEffect(() => {
    if (
      previousStatus === RequestStates.Loading &&
      status === RequestStates.Success &&
      onFinishedLoading
    ) {
      onFinishedLoading();
    }
  }, [onFinishedLoading, previousStatus, status]);

  const containerClasses = [
    styles.messageContainer,
    incoming
      ? styles.incomingMessageContainer
      : styles.outgoingMessageContainer,
  ]
    .filter(Boolean)
    .join(" ");

  const bubbleClasses = [
    styles.chatBubble,
    incoming && styles.incomingChatBubble,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Flex
      className={containerClasses}
      data-testid={testIds.wrapper}
      // E2E hook (AS-1921): lets specs scope text assertions to candidate
      // bubbles only — assistant acknowledgements may quote the candidate's
      // words, so role="status" + text alone is ambiguous.
      data-message-role={incoming ? "assistant" : "user"}
      direction="column"
    >
      {company && incoming && (
        <p className={styles.company} data-testid={testIds.company}>
          {company}
        </p>
      )}
      <div
        aria-busy={isLoading}
        className={bare ? styles.bareContainer : bubbleClasses}
        ref={containerRef}
        role="status"
      >
        {incoming && isLoading ? <TypingIndicator /> : children}
      </div>
    </Flex>
  );
};
